import { Box } from '@material-ui/core';
import { getPasswordRecoveryByUserId, PasswordRecovery } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { useCustomPagination } from '@/components/shared/hooks/useCustomPagination';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { PendingDataType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';

import { Font } from '../../../../../../../../../Font';
import { NemuruDialog } from '../../../../../../../../../NemuruDialog';
import { StandardButton } from '../../../../../../../../../StandardButton';
import { Text } from '../../../../../../../../../Text';
import { PaginatedListWrapper } from '../ChangeUser/components/PaginatedListWrapper';
import { PasswordRecoveryListTableBody } from './components/PasswordRecoveryListTableBody';
import { PasswordRecoveryListTableHeader } from './components/PasswordRecoveryListTableHeader';

interface PasswordRecoveryListProps {
  id: string;
  opened: boolean;
  setOpened: (boolean) => void;
  handleClose;
}

export const PasswordRecoveryList = ({ id, opened, setOpened, handleClose }: PasswordRecoveryListProps) => {
  const { palette } = useCustomTheme();
  const [passwordRecovery, setPasswordRecovery] = useState<PasswordRecovery[] | undefined>(undefined);

  const { page, rowsPerPage, onNext, onPrev, disableNext, disablePrev } = useCustomPagination({
    filteredResults: passwordRecovery,
    rowsPerPage: 4,
  });

  useEffect(() => {
    if (id) {
      getAndSetPasswordRecovery();
    }
    return () => {
      setPasswordRecovery(undefined);
    };
  }, [id]);

  const getAndSetPasswordRecovery = async () => {
    try {
      const passwordRecovery = await getPasswordRecoveryByUserId({ userId: id });
      setPasswordRecovery(passwordRecovery);
    } catch (error) {
      setPasswordRecovery([]);
    }
  };

  if (!passwordRecovery) return <></>;

  const paginatedResults = passwordRecovery.filter(
    (_, i) => i >= page * rowsPerPage - rowsPerPage && i < page * rowsPerPage,
  );

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      dialogProps={{ maxWidth: 'md' }}
      buttons={
        <>
          <StandardButton onClick={handleClose}>
            <Text tId="CLOSE" />
          </StandardButton>
        </>
      }
    >
      <Box marginBottom={2}>
        <Font variant="h3" color="textPrimary">
          <Text tId={'PASSWORD_RECOVERY_LIST'} />
        </Font>
      </Box>
      {
        <Box pt={3}>
          {passwordRecovery.length > 0 ? (
            <PaginatedListWrapper
              page={page}
              rowsPerPage={rowsPerPage}
              totalElementsValue={passwordRecovery.length}
              onNext={onNext}
              onPrev={onPrev}
              disableNext={disableNext}
              disablePrev={disablePrev}
              boxProps={{
                height: 'auto',
                paddingBottom: '65px',
                maxHeight: '280px',
                paddingLeft: '46px',
                bgcolor: palette.common.white,
              }}
            >
              <>
                <PasswordRecoveryListTableHeader />
                <PasswordRecoveryListTableBody passwordRecoveryList={paginatedResults} />
              </>
            </PaginatedListWrapper>
          ) : (
            <PendingDataWarning type={PendingDataType.PASSWORD_RECOVERY_NOT_FOUND} />
          )}
        </Box>
      }
    </NemuruDialog>
  );
};
